import React from 'react'
// import PropTypes from 'prop-types'
import { Transition } from 'react-transition-group'
import useForkRef from 'hooks/useForkRef'
import { reflow, getTransitionProps } from 'settings/app/uiHelpers'
import useTheme from 'hooks/useTheme'

function getScale(value) {
  return `scale(${ value }, ${ value ** 2 })`
}

const styles = {
  entering: {
    opacity: 1,
    transform: getScale(1),
  },
  entered: {
    opacity: 1,
    transform: 'none',
  },
}

/**
 * The Grow transition is used by the [Tooltip](/components/atoms/tooltip)
 * It uses [react-transition-group](https://github.com/reactjs/react-transition-group) inernally.
 */
const Grow = React.forwardRef((props, ref) => {
  const {
    children,
    in: inProp,
    onEnter,
    onEntered,
    onEntering,
    onExit,
    onExited,
    onExiting,
    style,
    timeout = 'auto',
    TransitionComponent = Transition,
    ...other
  } = props

  const timer = React.useRef()
  const autoTimeout = React.useRef()
  const theme = useTheme()

  const nodeRef = React.useRef(null)
  const foreignRef = useForkRef(children.ref, ref)
  const handleRef = useForkRef(nodeRef, foreignRef)

  const normalizedTransitionCallback = (callback) => (maybeIsAppearing) => {
    if (callback) {
      const node = nodeRef.current

      // onEnterXxx and onExitXxx callback have a different arguments.length value.
      if (maybeIsAppearing === undefined) {
        callback(node)
      } else {
        callback(node, maybeIsAppearing)
      }
    }
  }

  const handleEntering = normalizedTransitionCallback(onEntering)

  const handleEnter = normalizedTransitionCallback((node, isAppearing) => {
    reflow(node) // So the animation always start from the start.

    const { duration: transitionDuration, delay } = getTransitionProps(
      { style, timeout },
      {
        mode: 'enter',
      }
    )

    let duration
    if (timeout === 'auto') {
      duration = theme.transitions.getAutoHeightDuration(node.clientHeight)
      autoTimeout.current = duration
    } else {
      duration = transitionDuration
    }

    node.style.transition = [
      theme.transitions.create('opacity', {
        duration,
        delay,
      }),
      theme.transitions.create('transform', {
        duration: duration * 0.666,
        delay,
      }),
    ].join(',')

    if (onEnter) {
      onEnter(node, isAppearing)
    }
  })

  const handleEntered = normalizedTransitionCallback(onEntered)

  const handleExiting = normalizedTransitionCallback(onExiting)

  const handleExit = normalizedTransitionCallback((node) => {
    const { duration: transitionDuration, delay } = getTransitionProps(
      {
        style,
        timeout,
      },
      {
        mode: 'exit',
      }
    )

    let duration
    if (timeout === 'auto') {
      duration = theme.transitions.getAutoHeightDuration(node.clientHeight)
      autoTimeout.current = duration
    } else {
      duration = transitionDuration
    }

    node.style.transition = [
      theme.transitions.create('transform', {
        duration: duration * 0.666,
        delay: delay || duration * 0.333,
      }),
      theme.transitions.create('transform', {
        duration: duration * 0.666,
        delay: delay || duration * 0.333,
      }),
    ].join(',')

    node.style.opacity = '0'
    node.style.transform = getScale(0.75)

    if (onExit) {
      onExit(node)
    }
  })

  const handleExited = normalizedTransitionCallback(onExited)

  const addEndListener = (next) => {
    if (timeout === 'auto') {
      timer.current = setTimeout(next, autoTimeout.current || 0)
    }
  }

  React.useEffect(
    () => () => {
      clearTimeout(timer.current)
    },
    []
  )

  return (
    <TransitionComponent
      appear
      in={ inProp }
      nodeRef={ nodeRef }
      onEnter={ handleEnter }
      onEntered={ handleEntered }
      onEntering={ handleEntering }
      onExit={ handleExit }
      onExited={ handleExited }
      onExiting={ handleExiting }
      addEndListener={ addEndListener }
      timeout={ timeout === 'auto' ? null : timeout }
      { ...other }>
      {(state, childProps) =>
        React.cloneElement(children, {
          style: {
            opacity: 0,
            transform: getScale(0.75),
            visibility: state === 'exited' && !inProp ? 'hidden' : undefined,
            ...styles[state],
            ...style,
            ...children.props.style,
          },
          ref: handleRef,
          ...childProps,
        })
      }
    </TransitionComponent>
  )
})

export default Grow
